/**
 * Connecting a personal Claude subscription on a surface that can run
 * `claude auth login` itself: desktop, and any runtime with the CLI on PATH.
 *
 * Split out of ClaudeAuthenticationOptions, which had grown past the file-size
 * cap. Local and cloud sign-in are disjoint worlds that never render together,
 * so the seam costs nothing: one caller picks a side and this file owns the
 * local one.
 *
 * @module
 */
import type { useProviderAuth } from "../../hooks/useProviderAuth";

type ProviderAuth = ReturnType<typeof useProviderAuth>;

const ACTION_BUTTON_CLASS =
  "mt-2 inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60";

export function ClaudeLocalAuthOptions({
  providerAuth,
  code,
  setCode,
}: {
  readonly providerAuth: ProviderAuth;
  readonly code: string;
  readonly setCode: (value: string) => void;
}) {
  if (providerAuth.codePrompt) {
    return <ClaudePasteCodeForm providerAuth={providerAuth} code={code} setCode={setCode} />;
  }
  if (providerAuth.needsRuntime) {
    return <ClaudeNeedsRuntime providerAuth={providerAuth} />;
  }
  return <ClaudeConnectPrompt providerAuth={providerAuth} />;
}

function ClaudePasteCodeForm({
  providerAuth,
  code,
  setCode,
}: {
  readonly providerAuth: ProviderAuth;
  readonly code: string;
  readonly setCode: (value: string) => void;
}) {
  const trimmed = code.trim();
  return (
    <form
      className="min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed) {
          providerAuth.submitCode(trimmed);
        }
      }}
    >
      <p className="text-xs leading-5 text-muted-foreground">
        Open the authorization link, sign in to Claude, then paste the code it shows you.
      </p>
      {providerAuth.authorizeUrl ? (
        <a
          href={providerAuth.authorizeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          Open Claude authorization page
        </a>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Paste your code"
          className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 text-xs text-foreground placeholder:text-muted-foreground"
        />
        <button
          type="submit"
          disabled={providerAuth.actionBusy || trimmed.length === 0}
          className="inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          Connect
        </button>
      </div>
    </form>
  );
}

function ClaudeNeedsRuntime({ providerAuth }: { readonly providerAuth: ProviderAuth }) {
  return (
    <div className="min-w-0">
      <p className="text-xs leading-5 text-muted-foreground">
        Personal sign-in needs the Claude CLI. Install it here, or open Forge on a machine that has
        it, then retry, or keep using workspace credits.
      </p>
      <button
        type="button"
        disabled={providerAuth.actionBusy}
        onClick={() => providerAuth.connect("claudeAgent")}
        className={ACTION_BUTTON_CLASS}
      >
        Retry
      </button>
    </div>
  );
}

function ClaudeConnectPrompt({ providerAuth }: { readonly providerAuth: ProviderAuth }) {
  return (
    <div className="min-w-0">
      <p className="text-xs leading-5 text-muted-foreground">
        Connect your personal Claude subscription so the Claude agent runs on your own plan.
      </p>
      {providerAuth.failureMessage ? (
        <p className="mt-2 text-xs leading-5 text-destructive" role="alert">
          {providerAuth.failureMessage}
        </p>
      ) : null}
      <button
        type="button"
        disabled={providerAuth.actionBusy}
        onClick={() => providerAuth.connect("claudeAgent")}
        className={ACTION_BUTTON_CLASS}
      >
        {connectButtonLabel(providerAuth)}
      </button>
    </div>
  );
}

function connectButtonLabel(providerAuth: ProviderAuth): string {
  if (providerAuth.actionBusy) return "Connecting…";
  return providerAuth.failureMessage ? "Try again" : "Connect personal Claude";
}
